import React from "react";
import { asyncFieldOptionsFn } from "./dataloader";
import FormLabel from "shared/form/fields/formLabel";
import ContextEntityType from "shared/enums/ContextEntityType";
import FieldRenderSubType from "shared/enums/FieldRenderSubType";
import DynamicFieldConfigType from "../../types/DynamicFieldConfigType";
import { useDynamicFormRenderer } from "../../context/dynamicFormRendererContext";
import { useField, useFormikContext } from "formik";
import ErrorField from "shared/form/fields/errorField";
import { ORGANIZATION_SEARCH_MIN_CHARACTER_COUNT, ORGANIZATION_SEARCH_RESULT_COUNT } from "shared/utils/constants";
import FieldOption from "../../types/FieldOption";
import FormGroup from "shared/form/fields/formGroup";
import AsyncSelectDropdown from "shared/form/core/asyncSelectDropdown";

export default function CompetitorField(props: DynamicFieldConfigType) {
  const fieldName = React.useMemo(() => props.schemaObj.id, [props.schemaObj.id]);
  const [, meta] = useField({
    name: fieldName,
    validate: props.validationFn
  });
  const { setFieldTouched, setFieldValue, validateForm, validateOnChange } = useFormikContext();
  const { eventSeriesId, programParticipantId } = useDynamicFormRenderer();

  const initialOptions = React.useMemo(
    () => props.schemaObj.options?.map(item => ({ label: item.value, value: item.id })) || [],
    [props.schemaObj.options]
  );

  const [selectedValue, setSelectedValue] = React.useState(() => initialOptions);

  const loadOptionsHandler = async (inputValue: string) => {
    let options: Array<FieldOption> = [];
    if (inputValue.trim().length >= ORGANIZATION_SEARCH_MIN_CHARACTER_COUNT && eventSeriesId && programParticipantId) {
      try {
        options = (await asyncFieldOptionsFn({
          searchKey: inputValue,
          contextId: eventSeriesId,
          contextType: ContextEntityType.EVENT_SERIES,
          resultCount: ORGANIZATION_SEARCH_RESULT_COUNT,
          fieldRenderSubType: FieldRenderSubType.COMPETITOR,
          participantId: programParticipantId
        })) as unknown as Array<FieldOption>;
      } catch (err) {}
    }

    return options.map(item => ({ label: item.value, value: item.id }));
  };

  const changeHandler = React.useCallback(
    async values => {
      setSelectedValue(values);
      await setFieldValue(
        fieldName,
        values.map((item: FieldOption) => item.value),
        false
      );
      if (!meta.touched) {
        await setFieldTouched(fieldName, true, false);
      }
      validateOnChange && (await validateForm());
    },
    [setFieldValue, setFieldTouched, meta.touched, fieldName, validateForm, validateOnChange]
  );

  return (
    <FormGroup fullWidth={true} indented={props.schemaObj.indented}>
      <FormLabel
        htmlFor={fieldName}
        label={props.schemaObj.label}
        required={props.schemaObj.mandatory}
        infoLabel={props.schemaObj.infoLabel}
        additionalLabel={props.schemaObj.secondaryLabel}
      />

      <AsyncSelectDropdown
        name={fieldName}
        value={selectedValue}
        defaultOptions={initialOptions}
        isDisabled={props.schemaObj.disabled}
        loadOptions={loadOptionsHandler}
        onChange={changeHandler}
        isMulti
        cacheOptions
      />
      <ErrorField meta={meta} />
    </FormGroup>
  );
}
